import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, User, Flag, Calendar, CalendarClock, DollarSign, Truck, FileText, MessageSquare, Sparkles } from 'lucide-react';
import { updateSourcingInternal, getSourcingOptions } from '../../lib/api';

/**
 * Internal Work card — admin-only panel on the Sourcing Request detail
 * page. Never rendered on public surfaces. Patches fields one section
 * at a time via `PATCH /admin/sourcing-requests/{id}/internal`.
 *
 * Props:
 *   request:   the SR doc (must include id + all internal fields)
 *   onUpdate:  callback(updatedDoc) — fired after a successful save
 */
export default function InternalWorkCard({ request, onUpdate }) {
  const [draft, setDraft] = useState(() => snapshot(request));
  const [saving, setSaving] = useState(false);
  const [opts, setOpts] = useState({ priorities: ['low', 'normal', 'high', 'urgent'], admin_users: [] });

  useEffect(() => { setDraft(snapshot(request)); }, [request]);
  useEffect(() => {
    getSourcingOptions().then(r => setOpts(o => ({ ...o, ...r.data }))).catch(() => {});
  }, []);

  const dirty = Object.keys(draft).some(k => (draft[k] ?? '') !== (request[k] ?? ''));

  const save = async () => {
    setSaving(true);
    try {
      // Send only fields that changed.
      const payload = {};
      for (const k of Object.keys(draft)) {
        if ((draft[k] ?? '') !== (request[k] ?? '')) {
          payload[k] = k === 'estimated_value'
            ? (draft[k] === '' || draft[k] == null ? null : Number(draft[k]))
            : draft[k];
        }
      }
      const r = await updateSourcingInternal(request.id, payload);
      toast.success('Internal work updated');
      onUpdate?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="card-dark p-5 border-l-4 border-l-[#00D4FF]/60"
      data-testid="internal-work-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-semibold tracking-widest uppercase text-[#00D4FF]">Internal Work</div>
          <div className="text-xs text-white/40 mt-0.5">Admin-only — never visible to customers.</div>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary inline-flex items-center gap-2 text-xs disabled:opacity-50"
          data-testid="internal-work-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {saving ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field icon={User} label="Assigned To" testid="if-assigned">
          <select
            value={draft.assigned_to ?? ''}
            onChange={(e) => setDraft({ ...draft, assigned_to: e.target.value })}
            className="input"
            data-testid="if-assigned-select"
          >
            <option value="">— Unassigned —</option>
            {(opts.admin_users || []).map(e => <option key={e} value={e}>{e}</option>)}
            {/* Preserve current value if it's not in the admin list */}
            {draft.assigned_to && !(opts.admin_users || []).includes(draft.assigned_to) && (
              <option value={draft.assigned_to}>{draft.assigned_to} (existing)</option>
            )}
          </select>
        </Field>

        <Field icon={Flag} label="Priority" testid="if-priority">
          <select
            value={draft.priority ?? 'normal'}
            onChange={(e) => setDraft({ ...draft, priority: e.target.value })}
            className="input"
            data-testid="if-priority-select"
          >
            {opts.priorities.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </Field>

        <Field icon={CalendarClock} label="Next Follow-Up" testid="if-next-followup"
               autoUpdated={(request.auto_updated_fields || []).includes('next_follow_up_date')}>
          <input
            type="date"
            value={(draft.next_follow_up_date ?? '').slice(0, 10)}
            onChange={(e) => setDraft({ ...draft, next_follow_up_date: e.target.value || null })}
            className="input"
            data-testid="if-next-followup-input"
          />
        </Field>

        <Field icon={Calendar} label="Expected Close" testid="if-expected-close">
          <input
            type="date"
            value={(draft.expected_close_date ?? '').slice(0, 10)}
            onChange={(e) => setDraft({ ...draft, expected_close_date: e.target.value || null })}
            className="input"
          />
        </Field>

        <Field icon={DollarSign} label="Estimated Value (USD)" testid="if-value">
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.estimated_value ?? ''}
            onChange={(e) => setDraft({ ...draft, estimated_value: e.target.value })}
            placeholder="0.00"
            className="input"
            data-testid="if-value-input"
          />
        </Field>

        <Field icon={Calendar} label="Last Contacted" testid="if-last-contacted"
               autoUpdated={(request.auto_updated_fields || []).includes('last_contacted_date')}>
          <input
            type="date"
            value={(draft.last_contacted_date ?? '').slice(0, 10)}
            onChange={(e) => setDraft({ ...draft, last_contacted_date: e.target.value || null })}
            className="input"
          />
        </Field>

        <Field icon={Truck} label="Supplier Status" testid="if-supplier-status">
          <input
            type="text"
            value={draft.supplier_status ?? ''}
            onChange={(e) => setDraft({ ...draft, supplier_status: e.target.value })}
            placeholder="e.g. Awaiting cost from XYZ"
            className="input"
          />
        </Field>

        <Field icon={User} label="Customer Status" testid="if-customer-status">
          <input
            type="text"
            value={draft.customer_status ?? ''}
            onChange={(e) => setDraft({ ...draft, customer_status: e.target.value })}
            placeholder="e.g. Awaiting decision"
            className="input"
          />
        </Field>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
        <Field icon={MessageSquare} label="Internal Notes" testid="if-internal-notes">
          <textarea
            rows={3}
            value={draft.internal_notes ?? ''}
            onChange={(e) => setDraft({ ...draft, internal_notes: e.target.value })}
            placeholder="Strategy, internal decisions, things to remember…"
            className="input resize-y"
            data-testid="if-internal-notes-input"
          />
        </Field>

        <Field icon={FileText} label="Supplier Notes" testid="if-supplier-notes">
          <textarea
            rows={3}
            value={draft.supplier_notes ?? ''}
            onChange={(e) => setDraft({ ...draft, supplier_notes: e.target.value })}
            placeholder="Quotes received, lead times, supplier chatter…"
            className="input resize-y"
          />
        </Field>
      </div>

      <style>{`
        [data-testid="internal-work-card"] .input {
          width: 100%;
          padding: 8px 12px;
          background: #050505;
          border: 1px solid rgba(255,255,255,0.10);
          color: #fff;
          font-size: 13px;
        }
        [data-testid="internal-work-card"] .input:focus {
          outline: none;
          border-color: rgba(0,212,255,0.5);
        }
      `}</style>
    </div>
  );
}

function Field({ icon: Icon, label, testid, autoUpdated, children }) {
  return (
    <div data-testid={testid}>
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/55 mb-1">
        <Icon className="w-3.5 h-3.5 text-[#00D4FF]/70" />
        {label}
        {autoUpdated && (
          <span
            className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-[#00D4FF] text-[9px] font-medium normal-case tracking-normal"
            title="Auto-updated from Hostinger email sync — save any change to lock this field to your manual value."
            data-testid={`${testid}-auto-badge`}
          >
            <Sparkles className="w-2.5 h-2.5" />
            auto-updated
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

function snapshot(r) {
  return {
    assigned_to: r?.assigned_to ?? '',
    priority: r?.priority ?? 'normal',
    next_follow_up_date: r?.next_follow_up_date ?? '',
    last_contacted_date: r?.last_contacted_date ?? '',
    expected_close_date: r?.expected_close_date ?? '',
    estimated_value: r?.estimated_value ?? '',
    customer_status: r?.customer_status ?? '',
    supplier_status: r?.supplier_status ?? '',
    internal_notes: r?.internal_notes ?? '',
    supplier_notes: r?.supplier_notes ?? '',
  };
}
